import { z } from 'zod';

/** A complete batch is selectable only after all ten distinct ideas validate. */
const ideaBatch = (count: number) =>
  z
    .object({
      ideas: z
        .array(
          z
            .object({
              title: z.string().trim().min(3).max(100),
              hook: z.string().trim().min(8).max(300),
              angle: z.string().trim().min(8).max(400),
            })
            .strict()
        )
        .length(count),
    })
    .strict()
    .superRefine(({ ideas }, ctx) => {
      const keys = ideas.map(idea => idea.hook.toLowerCase().replace(/[^\p{L}\p{N}]/gu, ''));
      if (new Set(keys).size !== keys.length)
        ctx.addIssue({
          code: 'custom',
          path: ['ideas'],
          message: 'Give each idea a distinct hook.',
        });
    });
export const FirstPostIdeasSchema = ideaBatch(10);
/** Welcome keeps the same validated picker, with three focused directions. */
export const WelcomeIdeasSchema = ideaBatch(3);
export type FirstPostIdea = z.infer<typeof FirstPostIdeasSchema>['ideas'][number];

export const FirstPostMessageSchema = z.object({
  journeyId: z.literal('copilot-first-post'),
  boardId: z.string().min(1).max(200),
  stepId: z.string().min(1).max(80),
  label: z.string().trim().min(1).max(300).optional(),
  briefNodeId: z.string().min(1).max(200).optional(),
});

export const WelcomeMessageSchema = FirstPostMessageSchema.extend({
  journeyId: z.literal('welcome-to-poppy'),
});

/** UI labels never replace the full request sent to the model or saved with the thread. */
export function firstPostLabel(metadata: unknown): string | null {
  const result = FirstPostMessageSchema.safeParse((metadata as { coach?: unknown } | null)?.coach);
  return result.success ? (result.data.label ?? null) : null;
}

export function firstPostIdeas(value: unknown, welcome = false): FirstPostIdea[] | null {
  const parsed = (welcome ? WelcomeIdeasSchema : FirstPostIdeasSchema).safeParse(value);
  return parsed.success ? parsed.data.ideas : null;
}

/** Only a result belonging to the latest user request can advance the idea picker. */
export function latestFirstPostIdeas(
  messages: Array<{
    id: string;
    role: string;
    metadata?: unknown;
    parts: Array<{ type: string }>;
  }>,
  boardId: string,
  turnId: string | undefined
): { id: string; ideas: FirstPostIdea[] } | null {
  let lastUser = -1;
  messages.forEach((message, index) => {
    if (message.role === 'user') lastUser = index;
  });
  if (!turnId || messages[lastUser]?.id !== turnId) return null;
  const request = z
    .union([FirstPostMessageSchema, WelcomeMessageSchema])
    .safeParse((messages[lastUser]?.metadata as { coach?: unknown } | undefined)?.coach);
  if (
    !request.success ||
    request.data.boardId !== boardId ||
    !['ideas', 'ideas-working', 'ideas-help', 'idea-choice'].includes(request.data.stepId)
  )
    return null;
  for (const message of messages.slice(lastUser + 1)) {
    if (message.role !== 'assistant') continue;
    for (const raw of message.parts) {
      const part = raw as { type: string; state?: string; toolCallId?: string; output?: unknown };
      if (
        part.type !== 'tool-first_post_ideas' ||
        part.state !== 'output-available' ||
        !part.toolCallId
      )
        continue;
      const ideas = firstPostIdeas(part.output, request.data.journeyId === 'welcome-to-poppy');
      if (ideas) return { id: part.toolCallId, ideas };
    }
  }
  return null;
}

/** Keep the person's actual goal, selection or correction visible without the scaffolding. */
export function firstPostRequestLabel(
  stepId: string,
  context: Record<string, unknown>,
  fallback: string
): string {
  const field =
    stepId === 'interview'
      ? 'goals'
      : stepId === 'script-request'
        ? 'chosenIdea'
        : stepId === 'revision-request'
          ? 'feedback'
          : null;
  const prefix =
    stepId === 'interview'
      ? 'My goal: '
      : stepId === 'script-request'
        ? 'Write this idea: '
        : 'Update my script: ';
  const answer = field && typeof context[field] === 'string' ? String(context[field]).trim() : '';
  const label = answer ? prefix + answer : fallback;
  return label.length > 300 ? `${label.slice(0, 299)}…` : label;
}

/** Numbers for a list are fine; a monetary comparison or ranking needs supplied evidence. */
export function unsupportedIdeaClaims(ideas: FirstPostIdea[], evidence: string): string[] {
  const money = (text: string) =>
    [
      ...text.matchAll(
        /([$€£¥]|USD|EUR|GBP)\s*([0-9][0-9,.]*)|([0-9][0-9,.]*)\s*(dollars?|euros?|pounds?)/gi
      ),
    ].map(match => {
      const currency = (match[1] ?? match[4] ?? '').toLowerCase();
      const code = /^(\$|usd|dollars?)$/.test(currency)
        ? 'usd'
        : /^(€|eur|euros?)$/.test(currency)
          ? 'eur'
          : /^(£|gbp|pounds?)$/.test(currency)
            ? 'gbp'
            : currency;
      return `${code}:${Number((match[2] ?? match[3] ?? '').replace(/,/g, ''))}`;
    });
  const allowed = new Set(money(evidence));
  const issues = new Set<string>();
  for (const idea of ideas) {
    const text = [idea.title, idea.hook, idea.angle].join(' ');
    for (const claim of money(text))
      if (!allowed.has(claim)) issues.add(`Unsupported price ${claim}`);
    if (/#\s*1\b|\bnumber[ -]+one\b/i.test(text) && !/#\s*1\b|\bnumber[ -]+one\b/i.test(evidence))
      issues.add('Unsupported number-one ranking');
  }
  return [...issues];
}
